import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMainLanguageCode, getMPDataWithoutParams } from 'src/mentor/services'
import {
  getAssessmentRunResult,
  getRunContext,
  saveAssessmentRunProgressDraft,
  submitAssessmentRun,
} from 'src/mentor/services/practitionerAssessmentService'

dayjs.extend(utc)

const DRAFT_AUTOSAVE_DEBOUNCE_MS = 700
const DRAFT_AUTOSAVE_RETRY_MS = 1400
const STATUS_COLORS = {
  pass: 'success',
  fail: 'error',
  not_testable: 'warning',
}
const ASSIST_LANGUAGE_META = {
  en: { code: 'EN', label: 'English', glyph: 'A' },
  hi: { code: 'HI', label: 'Hindi', glyph: 'अ' },
  bn: { code: 'BN', label: 'Bengali', glyph: 'অ' },
  kn: { code: 'KN', label: 'Kannada', glyph: 'ಅ' },
}

function formatStatusLabel(status) {
  if (!status) return 'Not set'
  return String(status)
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function getAssistLanguageMeta(languageCode) {
  return ASSIST_LANGUAGE_META[languageCode] ?? {
    code: String(languageCode ?? '').toUpperCase(),
    label: languageCode,
    glyph: String(languageCode ?? '?').slice(0, 1).toUpperCase(),
  }
}

function normalizeItems(itemDefinitions) {
  if (!Array.isArray(itemDefinitions)) return []
  return [...itemDefinitions]
    .filter(Boolean)
    .sort((left, right) => {
      const leftOrder = Number.isFinite(Number(left?.order)) ? Number(left.order) : Number.MAX_SAFE_INTEGER
      const rightOrder = Number.isFinite(Number(right?.order)) ? Number(right.order) : Number.MAX_SAFE_INTEGER
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return String(left?.itemCode ?? '').localeCompare(String(right?.itemCode ?? ''))
    })
}

function getAssistLanguage(defaultAssistLanguages, primaryLanguage) {
  const languages = Array.isArray(defaultAssistLanguages) ? defaultAssistLanguages : []
  const firstDifferent = languages.find((language) => language && language !== primaryLanguage)
  if (firstDifferent) return firstDifferent
  if (primaryLanguage !== 'en') return 'en'
  return languages[0] ?? ''
}

function getTranslationValue(source, languageCode, fieldNames) {
  if (!source || !languageCode || !Array.isArray(fieldNames)) return null
  const languageEntry = source?.translations?.[languageCode]
  if (!languageEntry) return null
  for (const fieldName of fieldNames) {
    const value = languageEntry?.[fieldName]
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }
  }
  return null
}

function resolveItemCopy(item, primaryLanguage, assistLanguage) {
  const primaryLabel =
    getTranslationValue(item, primaryLanguage, ['questionText', 'title']) ??
    item?.label ??
    item?.itemCode ??
    'Assessment item'
  const primarySubText =
    getTranslationValue(item, primaryLanguage, ['subText']) ??
    item?.subText ??
    null
  const primaryHelpText =
    getTranslationValue(item, primaryLanguage, ['longDesc']) ??
    item?.helpText ??
    null
  const assistLabel =
    assistLanguage && assistLanguage !== primaryLanguage
      ? getTranslationValue(item, assistLanguage, ['questionText', 'title'])
      : null
  const assistSubText =
    assistLanguage && assistLanguage !== primaryLanguage
      ? getTranslationValue(item, assistLanguage, ['subText'])
      : null
  const assistHelpText =
    assistLanguage && assistLanguage !== primaryLanguage
      ? getTranslationValue(item, assistLanguage, ['longDesc'])
      : null
  return {
    primaryLabel,
    primarySubText,
    primaryHelpText,
    assistLabel,
    assistSubText,
    assistHelpText,
  }
}

function resolveDomainTitle(domain, primaryLanguage, assistLanguage) {
  const primaryTitle =
    getTranslationValue(domain, primaryLanguage, ['title']) ??
    domain?.title ??
    domain?.domainId ??
    'Domain'
  const assistTitle =
    assistLanguage && assistLanguage !== primaryLanguage
      ? getTranslationValue(domain, assistLanguage, ['title'])
      : null
  return { primaryTitle, assistTitle }
}

function getAgeMonthsFromDob(dateOfBirth) {
  if (!dateOfBirth) return null
  const dob = dayjs.utc(dateOfBirth)
  if (!dob.isValid()) return null
  const now = dayjs.utc()
  if (now.isBefore(dob)) return null
  return now.diff(dob, 'month')
}

function getAgeDaysFromDob(dateOfBirth) {
  if (!dateOfBirth) return null
  const dob = dayjs.utc(dateOfBirth)
  if (!dob.isValid()) return null
  const now = dayjs.utc()
  if (now.isBefore(dob)) return null
  return now.diff(dob, 'day')
}

function getAgeSnapshotFromDob(dateOfBirth) {
  return {
    months: getAgeMonthsFromDob(dateOfBirth),
    days: getAgeDaysFromDob(dateOfBirth),
  }
}

function getDateInputValue(dateOfBirth) {
  if (!dateOfBirth) return ''
  const dob = dayjs.utc(dateOfBirth)
  if (!dob.isValid()) return ''
  return dob.format('YYYY-MM-DD')
}

function isUsableDobValue(dateOfBirth) {
  return getDateInputValue(dateOfBirth) !== ''
}

function resolvePatientDobValue(bookedEventInfo, fieldName) {
  const candidates = [
    bookedEventInfo?.patientInfoData?.[fieldName],
    bookedEventInfo?.patientInfoData?.vital?.[fieldName],
    bookedEventInfo?.patientInfo?.[fieldName],
    bookedEventInfo?.patientInfo?.vital?.[fieldName],
    bookedEventInfo?.patient?.[fieldName],
    bookedEventInfo?.patient?.vital?.[fieldName],
    fieldName === 'dateOfBirth' ? bookedEventInfo?.patientDateOfBirth : null,
    bookedEventInfo?.[fieldName],
    bookedEventInfo?.patientPrefill?.vital?.[fieldName],
  ]
  return candidates.find((value) => isUsableDobValue(value)) ?? null
}

function resolvePreferredPatientDateOfBirth(providedDateOfBirth, estimatedDateOfBirth) {
  if (isUsableDobValue(providedDateOfBirth)) return providedDateOfBirth
  if (isUsableDobValue(estimatedDateOfBirth)) return estimatedDateOfBirth
  return null
}

function formatDateDisplay(dateOfBirth) {
  if (!dateOfBirth) return 'Not available'
  const dob = dayjs.utc(dateOfBirth)
  if (!dob.isValid()) return 'Not available'
  return dob.format('DD MMM YYYY')
}

function formatAgeDisplayFromMonths(ageMonths) {
  const numericAgeMonths = toNumeric(ageMonths)
  if (numericAgeMonths == null || numericAgeMonths < 0) return 'Not set'
  const wholeMonths = Math.round(numericAgeMonths)
  const years = Math.floor(wholeMonths / 12)
  const months = wholeMonths % 12
  if (years <= 0) return `${months} months`
  if (months === 0) return `${years} years`
  return `${years} years ${months} months`
}

function getAgePartsFromMonths(ageMonths) {
  const numericAgeMonths = toNumeric(ageMonths)
  if (numericAgeMonths == null || numericAgeMonths < 0) {
    return { years: '', months: '' }
  }
  const wholeMonths = Math.round(numericAgeMonths)
  return {
    years: String(Math.floor(wholeMonths / 12)),
    months: String(wholeMonths % 12),
  }
}

function buildAgeMonthsFromParts(yearsValue, monthsValue) {
  const hasYears = yearsValue !== '' && yearsValue != null
  const hasMonths = monthsValue !== '' && monthsValue != null
  if (!hasYears && !hasMonths) return ''

  const numericYears = hasYears ? Number(yearsValue) : 0
  const numericMonths = hasMonths ? Number(monthsValue) : 0
  if (!Number.isFinite(numericYears) || numericYears < 0) return ''
  if (!Number.isFinite(numericMonths) || numericMonths < 0) return ''

  const normalizedMonths = Math.min(11, Math.floor(numericMonths))
  const normalizedYears = Math.floor(numericYears)
  return (normalizedYears * 12) + normalizedMonths
}

function isEmptyStructuredResponse(response) {
  if (!response || typeof response !== 'object') return true
  const status = typeof response.status === 'string' ? response.status.trim() : ''
  const evidence = typeof response.evidence === 'string' ? response.evidence.trim() : ''
  const notes = typeof response.notes === 'string' ? response.notes.trim() : ''
  return !status && !evidence && !notes
}

function areStructuredResponsesEqual(left, right) {
  const leftValue = left && typeof left === 'object' ? left : {}
  const rightValue = right && typeof right === 'object' ? right : {}
  const keys = new Set([...Object.keys(leftValue), ...Object.keys(rightValue)])
  for (const key of keys) {
    if ((leftValue?.[key] ?? null) !== (rightValue?.[key] ?? null)) {
      return false
    }
  }
  return true
}

function normalizeStructuredResponses(initialStructuredResponses, items) {
  const allowedCodes = new Set((items ?? []).map((item) => item?.itemCode).filter(Boolean))
  const normalized = {}
  if (!initialStructuredResponses || typeof initialStructuredResponses !== 'object') {
    return normalized
  }
  Object.entries(initialStructuredResponses).forEach(([itemCode, response]) => {
    if (!allowedCodes.has(itemCode) || !response || typeof response !== 'object') return
    normalized[itemCode] = {
      status: response?.status ?? '',
      evidence: response?.evidence ?? '',
      notes: response?.notes ?? '',
    }
  })
  return normalized
}

function normalizeCustomRunData(initialCustomRunData, fallbackAgeMonths, fallbackAgeDays, options = {}) {
  const normalized = initialCustomRunData && typeof initialCustomRunData === 'object'
    ? { ...initialCustomRunData }
    : {}
  const hasUsablePatientDob = Boolean(options?.hasUsablePatientDob)
  const hasValidOverrideDateOfBirth = isUsableDobValue(normalized?.dateOfBirthOverride)
  const hasManualAgeValue = toNumeric(normalized?.chronologicalAgeMonths) != null

  if (normalized?.ageInputMode !== 'manual' && normalized?.ageInputMode !== 'dob') {
    normalized.ageInputMode = normalized?.ageSource === 'manual-age' || fallbackAgeMonths == null ? 'manual' : 'dob'
  }

  if (!hasUsablePatientDob && !hasValidOverrideDateOfBirth) {
    normalized.ageInputMode = 'manual'
  } else if (hasUsablePatientDob && normalized?.ageSource !== 'manual-age') {
    normalized.ageInputMode = 'dob'
  } else if (
    normalized?.ageInputMode === 'manual' &&
    !hasManualAgeValue &&
    normalized?.ageSource !== 'manual-age' &&
    (hasUsablePatientDob || hasValidOverrideDateOfBirth)
  ) {
    normalized.ageInputMode = 'dob'
  }

  if ((normalized?.chronologicalAgeMonths == null || normalized?.chronologicalAgeMonths === '') && fallbackAgeMonths != null) {
    normalized.chronologicalAgeMonths = fallbackAgeMonths
  }
  if ((normalized?.chronologicalAgeDays == null || normalized?.chronologicalAgeDays === '') && fallbackAgeDays != null) {
    normalized.chronologicalAgeDays = fallbackAgeDays
  }
  return normalized
}

function getSectionAgeRange(section) {
  const startAgeMonths = toNumeric(section?.domain?.config?.startAgeMonths)
  const endAgeMonths = toNumeric(section?.domain?.config?.endAgeMonths)
  return {
    startAgeMonths,
    endAgeMonths,
  }
}

function resolveRecommendedSection(domainSections, ageMonths) {
  if (!Array.isArray(domainSections) || domainSections.length === 0) return null
  const numericAgeMonths = toNumeric(ageMonths)
  if (numericAgeMonths == null) return domainSections[0]

  for (const section of domainSections) {
    const { startAgeMonths, endAgeMonths } = getSectionAgeRange(section)
    if (startAgeMonths == null && endAgeMonths == null) continue
    if (startAgeMonths != null && endAgeMonths != null) {
      if (numericAgeMonths >= startAgeMonths && (numericAgeMonths < endAgeMonths || startAgeMonths === endAgeMonths)) {
        return section
      }
      continue
    }
    if (startAgeMonths != null && numericAgeMonths >= startAgeMonths) return section
    if (endAgeMonths != null && numericAgeMonths < endAgeMonths) return section
  }

  return domainSections[domainSections.length - 1]
}

function clampNumber(value, minValue, maxValue) {
  const numericValue = toNumeric(value)
  if (numericValue == null) return null
  let nextValue = numericValue
  const numericMinValue = toNumeric(minValue)
  const numericMaxValue = toNumeric(maxValue)
  if (numericMinValue != null && nextValue < numericMinValue) nextValue = numericMinValue
  if (numericMaxValue != null && nextValue > numericMaxValue) nextValue = numericMaxValue
  return nextValue
}

function getProtocolAgeRange(ageRules) {
  return {
    minAgeMonths: toNumeric(ageRules?.minAgeMonths),
    maxAgeMonths: toNumeric(ageRules?.maxAgeMonths),
  }
}

function getSectionTitle(section) {
  return section?.domain?.title ?? section?.domain?.domainId ?? 'Age band'
}

function getSectionAgeHint(section) {
  const { startAgeMonths, endAgeMonths } = getSectionAgeRange(section)
  if (startAgeMonths != null && endAgeMonths != null) {
    return `${formatAgeDisplayFromMonths(startAgeMonths)} to ${formatAgeDisplayFromMonths(endAgeMonths)}`
  }
  if (startAgeMonths != null) return `From ${formatAgeDisplayFromMonths(startAgeMonths)}`
  if (endAgeMonths != null) return `Up to ${formatAgeDisplayFromMonths(endAgeMonths)}`
  return null
}

function findSectionIndexByDomainId(domainSections, domainId) {
  if (!Array.isArray(domainSections) || !domainId) return -1
  return domainSections.findIndex((section) => section?.domain?.domainId === domainId)
}

function resolveRecommendedStartAgeMonths(protocolType, ageMonths, minAgeMonths, maxAgeMonths) {
  const numericAgeMonths = toNumeric(ageMonths)
  if (numericAgeMonths == null) return null
  const protocol = String(protocolType ?? '').toUpperCase()
  const startAgeMonths = protocol === 'DST' ? numericAgeMonths - 24 : numericAgeMonths
  return clampNumber(startAgeMonths, minAgeMonths, maxAgeMonths)
}

function countAnsweredItems(sectionItems, structuredResponses) {
  if (!Array.isArray(sectionItems) || !sectionItems.length) return 0
  return sectionItems.reduce((count, item) => {
    const status = structuredResponses?.[item?.itemCode]?.status
    return status ? count + 1 : count
  }, 0)
}

function countUnansweredItemsInSectionRange(structuredResponses, domainSections, endSectionIndexInclusive) {
  if (!Array.isArray(domainSections) || endSectionIndexInclusive == null || endSectionIndexInclusive < 0) return 0
  let unansweredCount = 0
  for (let sectionIndex = 0; sectionIndex <= endSectionIndexInclusive; sectionIndex += 1) {
    const section = domainSections[sectionIndex]
    ;(section?.items ?? []).forEach((item) => {
      if (!structuredResponses?.[item?.itemCode]?.status) {
        unansweredCount += 1
      }
    })
  }
  return unansweredCount
}

function countAnsweredItemsInSectionRange(structuredResponses, domainSections, endSectionIndexInclusive) {
  if (!Array.isArray(domainSections) || endSectionIndexInclusive == null || endSectionIndexInclusive < 0) return 0
  let answeredCount = 0
  for (let sectionIndex = 0; sectionIndex <= endSectionIndexInclusive; sectionIndex += 1) {
    const section = domainSections[sectionIndex]
    ;(section?.items ?? []).forEach((item) => {
      if (structuredResponses?.[item?.itemCode]?.status) {
        answeredCount += 1
      }
    })
  }
  return answeredCount
}

function countNonPassItemsInSectionRange(structuredResponses, domainSections, endSectionIndexInclusive) {
  if (!Array.isArray(domainSections) || endSectionIndexInclusive == null || endSectionIndexInclusive < 0) return 0
  let nonPassCount = 0
  for (let sectionIndex = 0; sectionIndex <= endSectionIndexInclusive; sectionIndex += 1) {
    const section = domainSections[sectionIndex]
    ;(section?.items ?? []).forEach((item) => {
      const status = structuredResponses?.[item?.itemCode]?.status
      if (status && status !== 'pass') {
        nonPassCount += 1
      }
    })
  }
  return nonPassCount
}

function applyPassToSectionRange(structuredResponses, domainSections, endSectionIndexInclusive, options = {}) {
  const overwriteExisting = Boolean(options?.overwriteExisting)
  if (!Array.isArray(domainSections) || endSectionIndexInclusive == null || endSectionIndexInclusive < 0) {
    return { nextResponses: structuredResponses ?? {}, updatedCount: 0 }
  }
  const nextResponses = { ...(structuredResponses ?? {}) }
  let updatedCount = 0
  for (let sectionIndex = 0; sectionIndex <= endSectionIndexInclusive; sectionIndex += 1) {
    const section = domainSections[sectionIndex]
    ;(section?.items ?? []).forEach((item) => {
      if (!item?.itemCode) return
      const existing = nextResponses[item.itemCode] ?? {}
      if (existing?.status === 'pass') return
      if (existing?.status && !overwriteExisting) return
      nextResponses[item.itemCode] = {
        ...existing,
        status: 'pass',
      }
      updatedCount += 1
    })
  }
  return { nextResponses, updatedCount }
}

function hasMeaningfulCustomRunData(customRunData) {
  if (!customRunData || typeof customRunData !== 'object') return false
  const ignoredKeys = new Set([
    'chronologicalAgeMonths',
    'chronologicalAgeDays',
    'ageSource',
    'dateOfBirthUsed',
    'patientDateOfBirth',
    'estimatedDateOfBirth',
    'providedDateOfBirth',
  ])
  return Object.entries(customRunData).some(([key, value]) => {
    if (ignoredKeys.has(key)) return false
    return value != null && value !== ''
  })
}

function buildDomainSections(domainDefinitions, items) {
  const domainOrder = Array.isArray(domainDefinitions) ? domainDefinitions : []
  const byDomainId = new Map()
  domainOrder.forEach((domain) => {
    if (!domain?.domainId) return
    byDomainId.set(domain.domainId, { domain, items: [] })
  })
  ;(items ?? []).forEach((item) => {
    const domainId = item?.domainId || 'uncategorized'
    if (!byDomainId.has(domainId)) {
      byDomainId.set(domainId, {
        domain: { domainId, title: formatStatusLabel(domainId) },
        items: [],
      })
    }
    byDomainId.get(domainId).items.push(item)
  })
  return Array.from(byDomainId.values()).filter((section) => Array.isArray(section?.items) && section.items.length > 0)
}

function toNumeric(value) {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function roundTwo(value) {
  return Math.round(Number(value || 0) * 100) / 100
}

function getItemAgeEquivalentDays(item) {
  const configured = toNumeric(item?.config?.ageEquivalentTotalDays)
  if (configured != null) return configured
  const configuredMonths = toNumeric(item?.config?.ageEquivalentMonths)
  if (configuredMonths != null) return configuredMonths * 30
  return null
}

function buildPreviewMetrics({ items, structuredResponses, customProtocol, customRunData }) {
  const passStatuses = new Set((customProtocol?.scoringTables?.passStatuses ?? ['pass']).map((status) => String(status).toLowerCase()))
  const stopStatuses = new Set((customProtocol?.scoringTables?.stopStatuses ?? ['fail', 'not_testable']).map((status) => String(status).toLowerCase()))
  const failStatuses = new Set((customProtocol?.scoringTables?.failStatuses ?? ['fail']).map((status) => String(status).toLowerCase()))
  const responseEntries = items.map((item) => {
    const response = structuredResponses?.[item?.itemCode] ?? {}
    const status = typeof response?.status === 'string' ? response.status.toLowerCase() : ''
    return { item, response, status }
  })
  const totalItems = items.length
  const answeredItems = responseEntries.filter((entry) => entry.status).length
  const passCount = responseEntries.filter((entry) => passStatuses.has(entry.status)).length
  const failCount = responseEntries.filter((entry) => entry.status && !passStatuses.has(entry.status) && entry.status !== 'not_testable').length
  const notTestableCount = responseEntries.filter((entry) => entry.status === 'not_testable').length
  const completionRate = totalItems === 0 ? 0 : roundTwo((answeredItems / totalItems) * 100)
  const scorePercentage = totalItems === 0 ? 0 : roundTwo((passCount / totalItems) * 100)
  const protocolType = customProtocol?.protocolType
  const chronologicalAgeMonths = toNumeric(customRunData?.chronologicalAgeMonths)
  const chronologicalAgeDays = toNumeric(customRunData?.chronologicalAgeDays) ?? (chronologicalAgeMonths != null ? chronologicalAgeMonths * 30 : null)

  if (protocolType === 'DST' && customProtocol?.scoringTables?.mode === 'age-equivalent-first-ceiling') {
    const orderedEntries = [...responseEntries].sort((left, right) => {
      const leftAge = getItemAgeEquivalentDays(left.item) ?? Number.MAX_SAFE_INTEGER
      const rightAge = getItemAgeEquivalentDays(right.item) ?? Number.MAX_SAFE_INTEGER
      if (leftAge !== rightAge) return leftAge - rightAge
      return Number(left?.item?.order ?? 0) - Number(right?.item?.order ?? 0)
    })
    let developmentalAgeItem = null
    let highestPassedItem = null
    let firstCeilingFailure = null
    let ceilingReached = false
    let passAfterCeilingCount = 0
    let currentFailStreakCount = 0
    let currentFailStreakStartItem = null
    let currentFailStreakEndItem = null
    let maxConsecutiveFailCount = 0
    let maxConsecutiveFailStartItem = null
    let maxConsecutiveFailEndItem = null
    orderedEntries.forEach((entry) => {
      if (!entry.status) {
        currentFailStreakCount = 0
        currentFailStreakStartItem = null
        currentFailStreakEndItem = null
        return
      }
      if (failStatuses.has(entry.status)) {
        currentFailStreakCount += 1
        currentFailStreakStartItem = currentFailStreakStartItem ?? entry.item
        currentFailStreakEndItem = entry.item
        if (
          currentFailStreakCount > maxConsecutiveFailCount ||
          (
            currentFailStreakCount === maxConsecutiveFailCount &&
            (getItemAgeEquivalentDays(currentFailStreakEndItem) ?? -1) > (getItemAgeEquivalentDays(maxConsecutiveFailEndItem) ?? -1)
          )
        ) {
          maxConsecutiveFailCount = currentFailStreakCount
          maxConsecutiveFailStartItem = currentFailStreakStartItem
          maxConsecutiveFailEndItem = currentFailStreakEndItem
        }
      } else {
        currentFailStreakCount = 0
        currentFailStreakStartItem = null
        currentFailStreakEndItem = null
      }
      if (passStatuses.has(entry.status)) {
        highestPassedItem = entry.item
        if (!ceilingReached) {
          developmentalAgeItem = entry.item
        } else {
          passAfterCeilingCount += 1
        }
        return
      }
      if (!ceilingReached && stopStatuses.has(entry.status)) {
        ceilingReached = true
        firstCeilingFailure = entry.item
      }
    })
    const developmentalAgeDays = getItemAgeEquivalentDays(developmentalAgeItem) ?? getItemAgeEquivalentDays(highestPassedItem)
    const developmentalAgeMonths = developmentalAgeDays != null ? roundTwo(developmentalAgeDays / 30) : null
    const developmentalQuotient =
      developmentalAgeMonths != null && chronologicalAgeMonths != null && chronologicalAgeMonths > 0
        ? roundTwo((developmentalAgeMonths / chronologicalAgeMonths) * 100)
        : null

    return {
      protocolType,
      chronologicalAgeMonths,
      chronologicalAgeDays,
      developmentalAgeMonths,
      developmentalQuotient,
      totalItems,
      answeredItems,
      passCount,
      failCount,
      notTestableCount,
      completionRate,
      scorePercentage,
      highestPassedMilestone: highestPassedItem?.label ?? null,
      highestPassedMilestoneAgeDisplay: highestPassedItem?.config?.ageEquivalentDisplay ?? null,
      developmentalAgeMilestone: developmentalAgeItem?.label ?? highestPassedItem?.label ?? null,
      developmentalAgeDisplay: developmentalAgeItem?.config?.ageEquivalentDisplay ?? highestPassedItem?.config?.ageEquivalentDisplay ?? null,
      firstCeilingFailure: firstCeilingFailure?.label ?? null,
      firstCeilingFailureAgeDisplay: firstCeilingFailure?.config?.ageEquivalentDisplay ?? null,
      maxConsecutiveFailCount,
      maxConsecutiveFailStartAgeDisplay:
        maxConsecutiveFailStartItem?.config?.ageEquivalentDisplay ?? maxConsecutiveFailStartItem?.ageBand ?? null,
      maxConsecutiveFailEndAgeDisplay:
        maxConsecutiveFailEndItem?.config?.ageEquivalentDisplay ?? maxConsecutiveFailEndItem?.ageBand ?? null,
      passAfterCeilingCount,
    }
  }

  return {
    protocolType,
    chronologicalAgeMonths,
    chronologicalAgeDays,
    totalItems,
    answeredItems,
    passCount,
    failCount,
    notTestableCount,
    completionRate,
    scorePercentage,
  }
}

export default function CustomProtocolAssessmentRunner({
  assessmentSpec,
  clinicalAssessment,
  clinicalAssessmentId,
  psychAssessmentId,
  assessmentId,
  patientId,
  clinicId,
  bookedEventId,
  parentBookedEventId,
  initialDraftRunId = null,
  initialDraftRevision = null,
  initialStructuredResponses = {},
  initialCustomRunData = {},
  latestCompletedRunId = null,
  onSuccess,
  onBack,
  onViewResult,
  draftKey,
  bookedEventInfo,
}) {
  const clinicalId = clinicalAssessmentId ?? psychAssessmentId
  const customProtocol = assessmentSpec?.customProtocol ?? {}
  const protocolType = String(customProtocol?.protocolType ?? '').toUpperCase()
  const resolvedPatientId =
    patientId ??
    bookedEventInfo?.patientId ??
    bookedEventInfo?.patientInfoData?.id ??
    bookedEventInfo?.patientInfo?.id ??
    bookedEventInfo?.patient?.id ??
    null
  const resolvedClinicId =
    clinicId ??
    bookedEventInfo?.clinicId ??
    bookedEventInfo?.patientInfoData?.clinicId ??
    bookedEventInfo?.patientInfo?.clinicId ??
    bookedEventInfo?.patient?.clinicId ??
    null
  const [patientRecordInfo, setPatientRecordInfo] = useState(null)
  const items = useMemo(() => normalizeItems(customProtocol?.itemDefinitions), [customProtocol?.itemDefinitions])
  const domainSections = useMemo(
    () => buildDomainSections(customProtocol?.domainDefinitions, items),
    [customProtocol?.domainDefinitions, items],
  )
  const { minAgeMonths, maxAgeMonths } = useMemo(
    () => getProtocolAgeRange(customProtocol?.ageRules),
    [customProtocol?.ageRules],
  )
  useEffect(() => {
    let cancelled = false
    if (!resolvedPatientId) {
      setPatientRecordInfo(null)
      return () => {
        cancelled = true
      }
    }

    const hasPatientDobInEvent = Boolean(
      bookedEventInfo?.patientInfoData?.dateOfBirth ||
      bookedEventInfo?.patientInfo?.dateOfBirth ||
      bookedEventInfo?.patient?.dateOfBirth ||
      bookedEventInfo?.patientDateOfBirth ||
      bookedEventInfo?.dateOfBirth,
    )

    if (hasPatientDobInEvent) {
      setPatientRecordInfo(null)
      return () => {
        cancelled = true
      }
    }

    ;(async () => {
      try {
        let resolvedPatientRecord = null

        if (resolvedClinicId) {
          const mentorResponse = await getMPDataWithoutParams(
            `/api/secure/mentor/patient/list?patientId=${resolvedPatientId}&clinicId=${resolvedClinicId}`,
          )
          const mentorPayload = mentorResponse?.data
          if (Array.isArray(mentorPayload)) {
            resolvedPatientRecord = mentorPayload[0] ?? null
          } else if (Array.isArray(mentorPayload?.data)) {
            resolvedPatientRecord = mentorPayload.data[0] ?? null
          } else if (Array.isArray(mentorPayload?.patientInfo)) {
            resolvedPatientRecord = mentorPayload.patientInfo[0] ?? null
          }
        }

        if (!resolvedPatientRecord) {
          const response = await getDataWithParams('/api/secure/patient/get', { patientId: resolvedPatientId })
          if (response?.status === 200) {
            resolvedPatientRecord = response?.data?.data ?? response?.data ?? null
          }
        }

        if (cancelled) return
        setPatientRecordInfo(resolvedPatientRecord ?? null)
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch patient info for custom assessment runner', error)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [bookedEventInfo, resolvedClinicId, resolvedPatientId])

  const patientAwareBookedEventInfo = useMemo(() => {
    if (!patientRecordInfo) return bookedEventInfo
    return {
      ...(bookedEventInfo ?? {}),
      patientDateOfBirth: bookedEventInfo?.patientDateOfBirth ?? patientRecordInfo?.dateOfBirth ?? null,
      patientGender: bookedEventInfo?.patientGender ?? patientRecordInfo?.gender ?? null,
      patientNumber: bookedEventInfo?.patientNumber ?? patientRecordInfo?.patientNumber ?? null,
      patientInfoData: {
        ...(bookedEventInfo?.patientInfoData ?? {}),
        ...patientRecordInfo,
      },
      patientInfo: {
        ...(bookedEventInfo?.patientInfo ?? {}),
        ...patientRecordInfo,
      },
      patient: {
        ...(bookedEventInfo?.patient ?? {}),
        ...patientRecordInfo,
      },
    }
  }, [bookedEventInfo, patientRecordInfo])

  const primaryLanguage = getMainLanguageCode() || 'en'
  const assistLanguageOptions = useMemo(() => {
    const configured = Array.isArray(customProtocol?.defaultAssistLanguages) ? customProtocol.defaultAssistLanguages : []
    const options = new Set(configured.filter(Boolean))
    if (primaryLanguage !== 'en') options.add('en')
    return Array.from(options)
  }, [customProtocol?.defaultAssistLanguages, primaryLanguage])
  const [assistLanguage, setAssistLanguage] = useState(() =>
    getAssistLanguage(assistLanguageOptions, primaryLanguage),
  )
  const patientEstimatedDateOfBirth = useMemo(
    () => resolvePatientDobValue(patientAwareBookedEventInfo, 'dateOfBirth'),
    [patientAwareBookedEventInfo],
  )
  const patientProvidedDateOfBirth = useMemo(
    () => resolvePatientDobValue(patientAwareBookedEventInfo, 'dobProvided'),
    [patientAwareBookedEventInfo],
  )
  const defaultPatientDateOfBirth = useMemo(
    () => resolvePreferredPatientDateOfBirth(patientProvidedDateOfBirth, patientEstimatedDateOfBirth),
    [patientEstimatedDateOfBirth, patientProvidedDateOfBirth],
  )
  const patientAgeSnapshot = useMemo(
    () => getAgeSnapshotFromDob(defaultPatientDateOfBirth),
    [defaultPatientDateOfBirth],
  )
  const hasEstimatedDateOfBirth = isUsableDobValue(patientEstimatedDateOfBirth)
  const hasProvidedDateOfBirth = isUsableDobValue(patientProvidedDateOfBirth)
  const hasUsablePatientDob = hasProvidedDateOfBirth || hasEstimatedDateOfBirth
  const [structuredResponses, setStructuredResponses] = useState(() =>
    normalizeStructuredResponses(initialStructuredResponses, items),
  )
  const [customRunData, setCustomRunData] = useState(() =>
    normalizeCustomRunData(initialCustomRunData, patientAgeSnapshot?.months, patientAgeSnapshot?.days, {
      hasUsablePatientDob,
    }),
  )
  const overrideDateOfBirth = typeof customRunData?.dateOfBirthOverride === 'string'
    ? customRunData.dateOfBirthOverride
    : ''
  const ageInputMode = customRunData?.ageInputMode === 'manual' ? 'manual' : 'dob'
  const usingManualAge = ageInputMode === 'manual'
  const overrideAgeSnapshot = useMemo(
    () => getAgeSnapshotFromDob(overrideDateOfBirth),
    [overrideDateOfBirth],
  )
  const hasValidOverrideDateOfBirth = Boolean(overrideDateOfBirth) && overrideAgeSnapshot?.months != null
  const hasInvalidOverrideDateOfBirth = Boolean(overrideDateOfBirth) && !hasValidOverrideDateOfBirth
  const effectiveDateOfBirth = usingManualAge ? null : (hasValidOverrideDateOfBirth ? overrideDateOfBirth : (defaultPatientDateOfBirth || null))
  const effectiveAgeSnapshot = useMemo(
    () => (usingManualAge ? null : getAgeSnapshotFromDob(effectiveDateOfBirth)),
    [effectiveDateOfBirth, usingManualAge],
  )
  const effectiveChronologicalAgeMonths =
    usingManualAge
      ? toNumeric(customRunData?.chronologicalAgeMonths)
      : (effectiveAgeSnapshot?.months ?? toNumeric(customRunData?.chronologicalAgeMonths))
  const manualAgeParts = useMemo(
    () => getAgePartsFromMonths(customRunData?.chronologicalAgeMonths),
    [customRunData?.chronologicalAgeMonths],
  )
  const displayedChronologicalAgeParts = useMemo(
    () => (
      usingManualAge
        ? manualAgeParts
        : getAgePartsFromMonths(effectiveChronologicalAgeMonths)
    ),
    [effectiveChronologicalAgeMonths, manualAgeParts, usingManualAge],
  )
  const clampedChronologicalAgeMonths = useMemo(
    () => clampNumber(effectiveChronologicalAgeMonths, minAgeMonths, maxAgeMonths),
    [effectiveChronologicalAgeMonths, maxAgeMonths, minAgeMonths],
  )
  const actualAgeSection = useMemo(
    () => resolveRecommendedSection(domainSections, clampedChronologicalAgeMonths),
    [clampedChronologicalAgeMonths, domainSections],
  )
  const actualAgeSectionId = actualAgeSection?.domain?.domainId ?? null
  const actualAgeSectionTitle = getSectionTitle(actualAgeSection)
  const recommendedStartAgeMonths = useMemo(
    () => resolveRecommendedStartAgeMonths(protocolType, effectiveChronologicalAgeMonths, minAgeMonths, maxAgeMonths),
    [effectiveChronologicalAgeMonths, maxAgeMonths, minAgeMonths, protocolType],
  )
  const recommendedSection = useMemo(
    () => resolveRecommendedSection(domainSections, recommendedStartAgeMonths),
    [domainSections, recommendedStartAgeMonths],
  )
  const recommendedSectionId = recommendedSection?.domain?.domainId ?? null
  const recommendedSectionTitle = getSectionTitle(recommendedSection)
  const recommendedSectionIndex = useMemo(
    () => findSectionIndexByDomainId(domainSections, recommendedSectionId),
    [domainSections, recommendedSectionId],
  )
  const actualAgeSectionIndex = useMemo(
    () => findSectionIndexByDomainId(domainSections, actualAgeSectionId),
    [actualAgeSectionId, domainSections],
  )
  const sectionsBeforeRecommendedIndex = recommendedSectionIndex - 1
  const unansweredItemsBeforeRecommended = useMemo(
    () => countUnansweredItemsInSectionRange(structuredResponses, domainSections, sectionsBeforeRecommendedIndex),
    [domainSections, sectionsBeforeRecommendedIndex, structuredResponses],
  )
  const isAgeBelowRange = effectiveChronologicalAgeMonths != null && minAgeMonths != null && effectiveChronologicalAgeMonths < minAgeMonths
  const isAgeAboveRange = effectiveChronologicalAgeMonths != null && maxAgeMonths != null && effectiveChronologicalAgeMonths > maxAgeMonths
  const isAgeOutOfRange = isAgeBelowRange || isAgeAboveRange
  const ageRangeDisplay =
    minAgeMonths != null && maxAgeMonths != null
      ? `${formatAgeDisplayFromMonths(minAgeMonths)} to ${formatAgeDisplayFromMonths(maxAgeMonths)}`
      : null
  const scrollContainerRef = useRef(null)
  const sectionRefsRef = useRef({})
  const lastAutoScrolledSectionRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [draftRunState, setDraftRunState] = useState(() => ({
    draftRunId: initialDraftRunId ?? null,
    draftRevision:
      typeof initialDraftRevision === 'number' && Number.isFinite(initialDraftRevision)
        ? initialDraftRevision
        : null,
  }))
  const draftRunStateRef = useRef(draftRunState)
  const pendingDraftPatchRef = useRef({
    structuredResponsesPatch: {},
    removeStructuredResponseItemIds: new Set(),
    customRunDataPatch: {},
  })
  const previousStructuredResponsesRef = useRef(null)
  const previousCustomRunDataRef = useRef(null)
  const draftDiffInitializedRef = useRef(false)
  const draftPatchInFlightRef = useRef(false)
  const draftFlushTimerRef = useRef(null)
  const flushDraftProgressPatchRef = useRef(null)
  const draftLoadedRef = useRef(false)
  const [hasAttemptedDraftRestore, setHasAttemptedDraftRestore] = useState(() => !draftKey)
  const lastRunRef = useRef(null)
  const setupSectionRef = useRef(null)
  const [setupMode, setSetupMode] = useState(() => (protocolType === 'DST' ? 'pending' : 'confirmed'))
  const [earlierBandsDialog, setEarlierBandsDialog] = useState(null)

  useEffect(() => {
    draftRunStateRef.current = draftRunState
  }, [draftRunState])

  useEffect(() => {
    setAssistLanguage(getAssistLanguage(assistLanguageOptions, primaryLanguage))
  }, [assistLanguageOptions, primaryLanguage])

  useEffect(() => {
    setDraftRunState({
      draftRunId: initialDraftRunId ?? null,
      draftRevision:
        typeof initialDraftRevision === 'number' && Number.isFinite(initialDraftRevision)
          ? initialDraftRevision
          : null,
    })
  }, [assessmentId, bookedEventId, initialDraftRunId, initialDraftRevision])

  useEffect(() => {
    setStructuredResponses(normalizeStructuredResponses(initialStructuredResponses, items))
  }, [initialStructuredResponses, items])

  useEffect(() => {
    setSetupMode(protocolType === 'DST' ? 'pending' : 'confirmed')
    lastAutoScrolledSectionRef.current = null
  }, [assessmentId, bookedEventId, initialDraftRunId, initialDraftRevision, protocolType])

  useEffect(() => {
    setCustomRunData(
      normalizeCustomRunData(initialCustomRunData, patientAgeSnapshot?.months, patientAgeSnapshot?.days, {
        hasUsablePatientDob,
      }),
    )
  }, [hasUsablePatientDob, initialCustomRunData, patientAgeSnapshot?.days, patientAgeSnapshot?.months])

  useEffect(() => {
    const effectiveDateOfBirthValue = getDateInputValue(effectiveDateOfBirth)
    const estimatedDateOfBirthValue = getDateInputValue(patientEstimatedDateOfBirth)
    const providedDateOfBirthValue = getDateInputValue(patientProvidedDateOfBirth)
    const ageSource = usingManualAge
      ? 'manual-age'
      : hasValidOverrideDateOfBirth
        ? 'practitioner-dob-override'
        : providedDateOfBirthValue
          ? 'patient-dob-provided'
          : estimatedDateOfBirthValue
            ? 'patient-dob-estimated'
            : null

    setCustomRunData((previous) => {
      const current = previous && typeof previous === 'object' ? previous : {}
      let changed = false
      const next = { ...current }

      if ((next?.ageSource ?? null) !== ageSource) {
        next.ageSource = ageSource
        changed = true
      }
      if ((next?.estimatedDateOfBirth ?? null) !== (estimatedDateOfBirthValue || null)) {
        next.estimatedDateOfBirth = estimatedDateOfBirthValue || null
        changed = true
      }
      if ((next?.providedDateOfBirth ?? null) !== (providedDateOfBirthValue || null)) {
        next.providedDateOfBirth = providedDateOfBirthValue || null
        changed = true
      }
      if ((next?.dateOfBirthUsed ?? null) !== (usingManualAge ? null : (effectiveDateOfBirthValue || null))) {
        next.dateOfBirthUsed = usingManualAge ? null : (effectiveDateOfBirthValue || null)
        changed = true
      }
      if (!usingManualAge && effectiveAgeSnapshot?.months != null && next?.chronologicalAgeMonths !== effectiveAgeSnapshot.months) {
        next.chronologicalAgeMonths = effectiveAgeSnapshot.months
        changed = true
      }
      if (!usingManualAge && effectiveAgeSnapshot?.days != null && next?.chronologicalAgeDays !== effectiveAgeSnapshot.days) {
        next.chronologicalAgeDays = effectiveAgeSnapshot.days
        changed = true
      }
      if (usingManualAge) {
        const manualMonths = toNumeric(next?.chronologicalAgeMonths)
        const manualDays = manualMonths == null ? '' : manualMonths * 30
        if ((next?.chronologicalAgeDays ?? '') !== manualDays) {
          next.chronologicalAgeDays = manualDays
          changed = true
        }
      }

      return changed ? next : previous
    })
  }, [
    effectiveAgeSnapshot?.days,
    effectiveAgeSnapshot?.months,
    effectiveDateOfBirth,
    hasValidOverrideDateOfBirth,
    patientEstimatedDateOfBirth,
    patientProvidedDateOfBirth,
    usingManualAge,
  ])

  useEffect(() => {
    if (!hasUsablePatientDob && !hasValidOverrideDateOfBirth && !usingManualAge) {
      setCustomRunData((previous) => ({
        ...(previous && typeof previous === 'object' ? previous : {}),
        ageInputMode: 'manual',
      }))
    }
  }, [hasUsablePatientDob, hasValidOverrideDateOfBirth, usingManualAge])

  useEffect(() => {
    if (!draftKey || !items.length) {
      setHasAttemptedDraftRestore(true)
      return
    }
    if (draftLoadedRef.current) return
    try {
      const rawDraft = sessionStorage.getItem(draftKey)
      if (rawDraft) {
        const parsedDraft = JSON.parse(rawDraft)
        if (parsedDraft?.structuredResponses && typeof parsedDraft.structuredResponses === 'object') {
          setStructuredResponses((previous) => ({
            ...previous,
            ...normalizeStructuredResponses(parsedDraft.structuredResponses, items),
          }))
        }
        if (parsedDraft?.customRunData && typeof parsedDraft.customRunData === 'object') {
          setCustomRunData((previous) =>
            normalizeCustomRunData(
              {
                ...(previous && typeof previous === 'object' ? previous : {}),
                ...parsedDraft.customRunData,
              },
              patientAgeSnapshot?.months,
              patientAgeSnapshot?.days,
              { hasUsablePatientDob },
            ),
          )
        }
        const localDraftRunId = parsedDraft?.draftRunId ?? null
        const localDraftRevision =
          typeof parsedDraft?.draftRevision === 'number' && Number.isFinite(parsedDraft?.draftRevision)
            ? parsedDraft.draftRevision
            : null
        if (localDraftRunId || localDraftRevision != null) {
          setDraftRunState((previous) => ({
            draftRunId: localDraftRunId ?? previous?.draftRunId ?? null,
            draftRevision: localDraftRevision ?? previous?.draftRevision ?? null,
          }))
        }
      }
      draftLoadedRef.current = true
    } catch (error) {}
    setHasAttemptedDraftRestore(true)
  }, [draftKey, hasUsablePatientDob, items, patientAgeSnapshot?.days, patientAgeSnapshot?.months])

  useEffect(() => {
    if (!draftKey) return
    const timerId = setTimeout(() => {
      try {
        sessionStorage.setItem(
          draftKey,
          JSON.stringify({
            assessmentId,
            clinicalAssessmentId: clinicalId,
            bookedEventId,
            parentBookedEventId: parentBookedEventId ?? null,
            draftRunId: draftRunState?.draftRunId ?? null,
            draftRevision: draftRunState?.draftRevision ?? null,
            structuredResponses,
            customRunData,
          }),
        )
      } catch (error) {}
    }, 400)
    return () => clearTimeout(timerId)
  }, [
    assessmentId,
    bookedEventId,
    clinicalId,
    customRunData,
    draftKey,
    draftRunState,
    parentBookedEventId,
    structuredResponses,
  ])

  const previewMetrics = useMemo(
    () => buildPreviewMetrics({ items, structuredResponses, customProtocol, customRunData }),
    [customProtocol, customRunData, items, structuredResponses],
  )
  const completedItemCount = previewMetrics?.answeredItems ?? 0
  const requiresAgeSetup = protocolType === 'DST'
  const canBeginAssessment = !requiresAgeSetup || effectiveChronologicalAgeMonths != null
  const isSetupConfirmed = !requiresAgeSetup || setupMode === 'confirmed'
  const canShowAssessmentQuestions = canBeginAssessment && isSetupConfirmed

  const scrollToSection = useCallback((domainId, behavior = 'smooth') => {
    if (!domainId) return
    const sectionElement = sectionRefsRef.current?.[domainId]
    const container = scrollContainerRef.current
    if (!sectionElement || !container) return
    const top = Math.max(sectionElement.offsetTop - (protocolType === 'DST' ? 88 : 20), 0)
    container.scrollTo({ top, behavior })
  }, [protocolType])

  const scrollToRecommendedSection = useCallback(
    (behavior = 'smooth') => {
      if (!recommendedSectionId) return
      scrollToSection(recommendedSectionId, behavior)
    },
    [recommendedSectionId, scrollToSection],
  )

  const applyPassShortcutThroughSection = useCallback(
    (endSectionIndexInclusive, options = {}) => {
      const { nextResponses, updatedCount } = applyPassToSectionRange(
        structuredResponses,
        domainSections,
        endSectionIndexInclusive,
        options,
      )
      if (updatedCount > 0) {
        setStructuredResponses(nextResponses)
        setSubmitError(null)
      }
      return updatedCount
    },
    [domainSections, structuredResponses],
  )

  const clearResponsesThroughSection = useCallback(
    (endSectionIndexInclusive) => {
      if (endSectionIndexInclusive == null || endSectionIndexInclusive < 0) return 0
      let clearedCount = 0
      setStructuredResponses((previous) => {
        const current = previous && typeof previous === 'object' ? previous : {}
        const next = { ...current }
        for (let sectionIndex = 0; sectionIndex <= endSectionIndexInclusive; sectionIndex += 1) {
          const section = domainSections?.[sectionIndex]
          ;(section?.items ?? []).forEach((item) => {
            const itemCode = item?.itemCode
            if (!itemCode || !next[itemCode] || isEmptyStructuredResponse(next[itemCode])) return
            delete next[itemCode]
            clearedCount += 1
          })
        }
        return clearedCount > 0 ? next : previous
      })
      if (clearedCount > 0) {
        setSubmitError(null)
      }
      return clearedCount
    },
    [domainSections],
  )

  const handleEarlierBandsPassClick = useCallback(
    (endSectionIndexInclusive, currentSectionTitle, currentSectionDomainId) => {
      if (endSectionIndexInclusive == null || endSectionIndexInclusive < 0) return
      const nonPassCount = countNonPassItemsInSectionRange(structuredResponses, domainSections, endSectionIndexInclusive)
      if (nonPassCount > 0) {
        setEarlierBandsDialog({
          endSectionIndexInclusive,
          currentSectionTitle,
          currentSectionDomainId,
          nonPassCount,
        })
        return
      }
      const updatedCount = applyPassShortcutThroughSection(endSectionIndexInclusive, { overwriteExisting: true })
      if (updatedCount > 0 && currentSectionDomainId) {
        window.setTimeout(() => {
          scrollToSection(currentSectionDomainId)
        }, 80)
      }
    },
    [applyPassShortcutThroughSection, domainSections, scrollToSection, structuredResponses],
  )

  const confirmEarlierBandsPass = useCallback(
    (overwriteExisting) => {
      if (!earlierBandsDialog) return
      const { endSectionIndexInclusive, currentSectionDomainId } = earlierBandsDialog
      setEarlierBandsDialog(null)
      const updatedCount = applyPassShortcutThroughSection(endSectionIndexInclusive, { overwriteExisting })
      if (updatedCount > 0 && currentSectionDomainId) {
        window.setTimeout(() => {
          scrollToSection(currentSectionDomainId)
        }, 80)
      }
    },
    [applyPassShortcutThroughSection, earlierBandsDialog, scrollToSection],
  )

  const handleEarlierBandsReset = useCallback(
    (endSectionIndexInclusive, currentSectionDomainId) => {
      const clearedCount = clearResponsesThroughSection(endSectionIndexInclusive)
      if (clearedCount > 0 && currentSectionDomainId) {
        window.setTimeout(() => {
          scrollToSection(currentSectionDomainId)
        }, 80)
      }
    },
    [clearResponsesThroughSection, scrollToSection],
  )

  const handleUseDobAge = useCallback(() => {
    setCustomRunData((previous) => ({
      ...(previous ?? {}),
      ageInputMode: 'dob',
    }))
  }, [])

  const handleUseManualAge = useCallback(() => {
    setCustomRunData((previous) => {
      const current = previous && typeof previous === 'object' ? previous : {}
      const currentMonths = toNumeric(current?.chronologicalAgeMonths)
      const nextMonths = currentMonths ?? effectiveChronologicalAgeMonths ?? ''
      return {
        ...current,
        ageInputMode: 'manual',
        chronologicalAgeMonths: nextMonths,
        chronologicalAgeDays: nextMonths === '' ? '' : nextMonths * 30,
      }
    })
  }, [effectiveChronologicalAgeMonths])

  const handleConfirmSetup = useCallback(() => {
    if (!canBeginAssessment) return
    setSetupMode('confirmed')
    setSubmitError(null)
    window.setTimeout(() => {
      scrollToRecommendedSection('auto')
    }, 120)
  }, [canBeginAssessment, scrollToRecommendedSection])

  const handleEditSetup = useCallback(() => {
    setEarlierBandsDialog(null)
    setSetupMode('editing')
    lastAutoScrolledSectionRef.current = null
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (!canShowAssessmentQuestions || !recommendedSectionId) return
    const alreadyFocusedSection = lastAutoScrolledSectionRef.current
    if (alreadyFocusedSection === recommendedSectionId) return
    const timerId = window.setTimeout(() => {
      scrollToSection(recommendedSectionId, alreadyFocusedSection ? 'smooth' : 'auto')
      lastAutoScrolledSectionRef.current = recommendedSectionId
    }, 120)
    return () => window.clearTimeout(timerId)
  }, [canShowAssessmentQuestions, recommendedSectionId, scrollToSection])

  const recommendedSectionAgeHint = getSectionAgeHint(recommendedSection)
  const actualSectionAgeHint = getSectionAgeHint(actualAgeSection)

  const scheduleDraftPatchFlush = useCallback(
    (delayMs = DRAFT_AUTOSAVE_DEBOUNCE_MS) => {
      if (!bookedEventId || !clinicalId || !assessmentId || submitSuccess) return
      if (draftFlushTimerRef.current) {
        clearTimeout(draftFlushTimerRef.current)
      }
      draftFlushTimerRef.current = setTimeout(() => {
        if (typeof flushDraftProgressPatchRef.current === 'function') {
          void flushDraftProgressPatchRef.current()
        }
      }, delayMs)
    },
    [assessmentId, bookedEventId, clinicalId, submitSuccess],
  )

  const queueStructuredResponsePatch = useCallback(
    (itemCode, value) => {
      if (!itemCode || !bookedEventId || !clinicalId || !assessmentId || submitSuccess) return
      const pending = pendingDraftPatchRef.current
      if (isEmptyStructuredResponse(value)) {
        delete pending.structuredResponsesPatch[itemCode]
        pending.removeStructuredResponseItemIds.add(itemCode)
      } else {
        pending.structuredResponsesPatch[itemCode] = value
        pending.removeStructuredResponseItemIds.delete(itemCode)
      }
      scheduleDraftPatchFlush()
    },
    [assessmentId, bookedEventId, clinicalId, scheduleDraftPatchFlush, submitSuccess],
  )

  const queueCustomRunDataPatch = useCallback(
    (key, value) => {
      if (!key || !bookedEventId || !clinicalId || !assessmentId || submitSuccess) return
      pendingDraftPatchRef.current.customRunDataPatch[key] = value == null || value === '' ? null : value
      scheduleDraftPatchFlush()
    },
    [assessmentId, bookedEventId, clinicalId, scheduleDraftPatchFlush, submitSuccess],
  )

  const flushDraftProgressPatch = useCallback(
    async ({ force = false } = {}) => {
      if (!bookedEventId || !clinicalId || !assessmentId || submitSuccess) return true
      if (draftPatchInFlightRef.current) {
        if (!force) scheduleDraftPatchFlush(DRAFT_AUTOSAVE_RETRY_MS)
        return false
      }
      const pending = pendingDraftPatchRef.current
      const structuredResponsesPatch = { ...pending.structuredResponsesPatch }
      const removeStructuredResponseItemIds = Array.from(pending.removeStructuredResponseItemIds)
      const customRunDataPatch = { ...pending.customRunDataPatch }
      if (
        Object.keys(structuredResponsesPatch).length === 0 &&
        removeStructuredResponseItemIds.length === 0 &&
        Object.keys(customRunDataPatch).length === 0
      ) {
        return true
      }
      pendingDraftPatchRef.current = {
        structuredResponsesPatch: {},
        removeStructuredResponseItemIds: new Set(),
        customRunDataPatch: {},
      }
      draftPatchInFlightRef.current = true
      try {
        const latestDraftState = draftRunStateRef.current
        const payload = {
          runnerType: 'CUSTOM_PROTOCOL',
          protocolTemplateId: customProtocol?.protocolTemplateId ?? assessmentSpec?.protocolTemplateId,
          scoringProfileId: customProtocol?.scoringProfileId ?? assessmentSpec?.scoringProfileId,
        }
        const hasKnownDraft =
          Boolean(latestDraftState?.draftRunId) &&
          typeof latestDraftState?.draftRevision === 'number' &&
          Number.isFinite(latestDraftState?.draftRevision)
        if (hasKnownDraft) {
          payload.draftRunId = latestDraftState.draftRunId
          payload.draftRevision = latestDraftState.draftRevision
        } else {
          payload.bookedEventId = bookedEventId
          if (parentBookedEventId) payload.parentBookedEventId = parentBookedEventId
          payload.clinicalAssessmentId = clinicalId
          payload.assessmentId = assessmentId
        }
        if (Object.keys(structuredResponsesPatch).length > 0) payload.structuredResponsesPatch = structuredResponsesPatch
        if (removeStructuredResponseItemIds.length > 0) payload.removeStructuredResponseItemIds = removeStructuredResponseItemIds
        if (Object.keys(customRunDataPatch).length > 0) payload.customRunDataPatch = customRunDataPatch

        const result = await saveAssessmentRunProgressDraft(payload)
        if (result?.success) {
          setDraftRunState((previous) => ({
            draftRunId: result?.draftRunId ?? previous?.draftRunId ?? null,
            draftRevision:
              typeof result?.draftRevision === 'number' && Number.isFinite(result?.draftRevision)
                ? result.draftRevision
                : (previous?.draftRevision ?? null),
          }))
          return true
        }

        if (result?.assessmentCompleted || result?.code === 4038) {
          return false
        }

        if (result?.conflict) {
          if (result?.latestDraftStructuredResponses && typeof result.latestDraftStructuredResponses === 'object') {
            setStructuredResponses(normalizeStructuredResponses(result.latestDraftStructuredResponses, items))
          }
          if (result?.latestDraftCustomRunData && typeof result.latestDraftCustomRunData === 'object') {
            setCustomRunData((previous) => ({
              ...previous,
              ...result.latestDraftCustomRunData,
            }))
          }
          setDraftRunState((previous) => ({
            draftRunId: result?.draftRunId ?? previous?.draftRunId ?? null,
            draftRevision:
              typeof result?.draftRevision === 'number' && Number.isFinite(result?.draftRevision)
                ? result.draftRevision
                : (previous?.draftRevision ?? null),
          }))
        }

        const retryPending = pendingDraftPatchRef.current
        Object.entries(structuredResponsesPatch).forEach(([itemCode, value]) => {
          retryPending.structuredResponsesPatch[itemCode] = value
          retryPending.removeStructuredResponseItemIds.delete(itemCode)
        })
        removeStructuredResponseItemIds.forEach((itemCode) => {
          delete retryPending.structuredResponsesPatch[itemCode]
          retryPending.removeStructuredResponseItemIds.add(itemCode)
        })
        Object.entries(customRunDataPatch).forEach(([key, value]) => {
          retryPending.customRunDataPatch[key] = value
        })
        scheduleDraftPatchFlush(result?.conflict ? 0 : DRAFT_AUTOSAVE_RETRY_MS)
        return false
      } finally {
        draftPatchInFlightRef.current = false
      }
    },
    [
      assessmentId,
      assessmentSpec?.protocolTemplateId,
      assessmentSpec?.scoringProfileId,
      bookedEventId,
      clinicalId,
      customProtocol?.protocolTemplateId,
      customProtocol?.scoringProfileId,
      items,
      parentBookedEventId,
      scheduleDraftPatchFlush,
      submitSuccess,
    ],
  )

  useEffect(() => {
    flushDraftProgressPatchRef.current = flushDraftProgressPatch
  }, [flushDraftProgressPatch])

  useEffect(() => {
    if (!hasAttemptedDraftRestore) return
    if (submitSuccess) return
    if (!draftDiffInitializedRef.current) {
      previousStructuredResponsesRef.current = structuredResponses
      previousCustomRunDataRef.current = customRunData
      draftDiffInitializedRef.current = true
      return
    }

    const previousStructuredResponses = previousStructuredResponsesRef.current ?? {}
    const currentStructuredResponses = structuredResponses ?? {}
    const itemCodes = new Set([...Object.keys(previousStructuredResponses), ...Object.keys(currentStructuredResponses)])
    itemCodes.forEach((itemCode) => {
      if (!areStructuredResponsesEqual(previousStructuredResponses[itemCode], currentStructuredResponses[itemCode])) {
        queueStructuredResponsePatch(itemCode, currentStructuredResponses[itemCode])
      }
    })
    previousStructuredResponsesRef.current = currentStructuredResponses

    const previousCustomData = previousCustomRunDataRef.current ?? {}
    const currentCustomData = customRunData ?? {}
    const customKeys = new Set([...Object.keys(previousCustomData), ...Object.keys(currentCustomData)])
    customKeys.forEach((key) => {
      if ((previousCustomData?.[key] ?? null) !== (currentCustomData?.[key] ?? null)) {
        queueCustomRunDataPatch(key, currentCustomData?.[key] ?? null)
      }
    })
    previousCustomRunDataRef.current = currentCustomData
  }, [
    customRunData,
    hasAttemptedDraftRestore,
    queueCustomRunDataPatch,
    queueStructuredResponsePatch,
    structuredResponses,
    submitSuccess,
  ])

  useEffect(() => {
    return () => {
      if (draftFlushTimerRef.current) {
        clearTimeout(draftFlushTimerRef.current)
      }
    }
  }, [])

  const updateStructuredResponse = useCallback((itemCode, patch) => {
    setStructuredResponses((previous) => {
      const current = previous?.[itemCode] ?? {}
      return {
        ...previous,
        [itemCode]: {
          ...current,
          ...patch,
        },
      }
    })
  }, [])

  const clearAllResponses = useCallback(() => {
    if (!window.confirm('Clear all captured milestone responses? Age and DOB settings will be kept.')) {
      return
    }
    if (draftKey) {
      try {
        sessionStorage.removeItem(draftKey)
      } catch (error) {}
    }
    setStructuredResponses({})
    setEarlierBandsDialog(null)
    setSubmitError(null)
  }, [draftKey])

  const openCompletedResult = useCallback(
    async (completedRunId, fallbackRun = null, fallbackReport = null) => {
      if (!onViewResult || !completedRunId) return false
      const runResult = await getAssessmentRunResult({ completedRunId })
      if (!runResult?.error) {
        onViewResult(assessmentId, assessmentSpec?.title, {
          data: {
            assessmentRun: runResult?.assessmentRun ?? null,
            assessmentRunView: runResult?.assessmentRunView ?? null,
            assessmentReport: runResult?.assessmentReport ?? null,
          },
        })
        return true
      }
      onViewResult(assessmentId, assessmentSpec?.title, {
        data: {
          assessmentRun: fallbackRun,
          assessmentRunView: null,
          assessmentReport: fallbackReport,
        },
      })
      return true
    },
    [assessmentId, assessmentSpec?.title, onViewResult],
  )

  const handleSubmit = useCallback(async () => {
    if (!bookedEventId) {
      setSubmitError('Assessment booking id is missing. Please refresh and try again.')
      return
    }
    if (!items.length) {
      setSubmitError('No custom protocol items are available in this assessment.')
      return
    }
    if (protocolType === 'DST' && effectiveChronologicalAgeMonths == null) {
      setSubmitError('Chronological age is required for DST scoring. Use a valid DOB or enter age in years and months before submitting.')
      return
    }
    if (
      protocolType === 'DST' &&
      unansweredItemsBeforeRecommended > 0 &&
      previewMetrics?.passCount > 0
    ) {
      const shouldAutoFillEarlierMilestones = window.confirm(
        `There are ${unansweredItemsBeforeRecommended} earlier DST milestones still unanswered before the recommended start band. ` +
          'DST basal-level workflows usually treat earlier milestones as PASS once continuous attainment is established. ' +
          'Press OK to mark those earlier unanswered milestones as PASS now, or Cancel to review them before submitting.',
      )
      if (!shouldAutoFillEarlierMilestones) {
        scrollToRecommendedSection()
        return
      }
    }
    let structuredResponsesToSubmit = structuredResponses
    if (
      protocolType === 'DST' &&
      unansweredItemsBeforeRecommended > 0 &&
      previewMetrics?.passCount > 0
    ) {
      const { nextResponses, updatedCount } = applyPassToSectionRange(
        structuredResponses,
        domainSections,
        sectionsBeforeRecommendedIndex,
      )
      if (updatedCount > 0) {
        structuredResponsesToSubmit = nextResponses
        setStructuredResponses(nextResponses)
        setSubmitError(null)
      }
    }
    if (draftFlushTimerRef.current) {
      clearTimeout(draftFlushTimerRef.current)
      draftFlushTimerRef.current = null
    }
    await flushDraftProgressPatch({ force: true })
    setSubmitting(true)
    setSubmitError(null)
    try {
      if (latestCompletedRunId) {
        const movedToResult = await openCompletedResult(latestCompletedRunId)
        if (movedToResult) return
      }

      const payload = {
        bookedEventId,
        clinicalAssessmentId: clinicalId,
        assessmentId,
        runnerType: 'CUSTOM_PROTOCOL',
        protocolTemplateId: customProtocol?.protocolTemplateId ?? assessmentSpec?.protocolTemplateId,
        scoringProfileId: customProtocol?.scoringProfileId ?? assessmentSpec?.scoringProfileId,
        structuredResponses: structuredResponsesToSubmit,
        customRunData,
      }
      if (parentBookedEventId) payload.parentBookedEventId = parentBookedEventId
      const response = await submitAssessmentRun(payload)
      const responseCode = response?.data?.code ?? response?.response?.data?.code
      const responseMessage = response?.data?.message ?? response?.response?.data?.message
      const responseData = response?.data?.data ?? response?.response?.data?.data ?? response?.data ?? response?.response?.data
      const success = response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)
      if (success) {
        lastRunRef.current = responseData ?? null
        pendingDraftPatchRef.current = {
          structuredResponsesPatch: {},
          removeStructuredResponseItemIds: new Set(),
          customRunDataPatch: {},
        }
        setDraftRunState({ draftRunId: null, draftRevision: null })
        if (draftKey) {
          try {
            sessionStorage.removeItem(draftKey)
          } catch (error) {}
        }
        setSubmitSuccess(true)
        return
      }

      if (responseCode === 40992) {
        const runContext = await getRunContext({
          bookedEventId,
          parentBookedEventId,
          clinicalAssessmentId: clinicalId,
          assessmentId,
        })
        const completedRunId = runContext?.latestCompletedRunId ?? null
        const isCompleted = runContext?.assessmentCompleted === true || Boolean(completedRunId)
        if (isCompleted && completedRunId) {
          const movedToResult = await openCompletedResult(
            completedRunId,
            runContext?.latestCompletedAssessmentRun ?? null,
            null,
          )
          if (movedToResult) return
        }
        setSubmitError('This assessment is already completed. Opening the latest result was not possible.')
        return
      }

      setSubmitError(responseMessage || 'Failed to submit this assessment.')
    } catch (error) {
      setSubmitError('Failed to submit this assessment.')
    } finally {
      setSubmitting(false)
    }
  }, [
    assessmentId,
    assessmentSpec?.protocolTemplateId,
    assessmentSpec?.scoringProfileId,
    bookedEventId,
    clinicalId,
    customProtocol?.protocolTemplateId,
    customProtocol?.scoringProfileId,
    customRunData,
    draftKey,
    flushDraftProgressPatch,
    effectiveChronologicalAgeMonths,
    items.length,
    latestCompletedRunId,
    openCompletedResult,
    parentBookedEventId,
    domainSections,
    previewMetrics?.passCount,
    protocolType,
    scrollToRecommendedSection,
    sectionsBeforeRecommendedIndex,
    structuredResponses,
    unansweredItemsBeforeRecommended,
  ])

  const handleBackAfterSubmit = useCallback(() => {
    onSuccess?.(lastRunRef.current)
  }, [onSuccess])

  const handleBackToList = useCallback(() => {
    const hasCapturedData =
      Object.keys(structuredResponses ?? {}).length > 0 ||
      hasMeaningfulCustomRunData(customRunData)
    if (hasCapturedData && !window.confirm('Leave without submitting? Progress is saved as draft.')) {
      return
    }
    onBack?.()
  }, [customRunData, onBack, structuredResponses])

  const runnerTitle = assessmentSpec?.title ?? clinicalAssessment?.title ?? 'Custom assessment'
  const runnerSubtitle =
    protocolType === 'DST'
      ? 'Guided DST milestone capture.'
      : (assessmentSpec?.subText ?? customProtocol?.translations?.[primaryLanguage]?.subText ?? 'Capture structured observations domain by domain.')
  const ageSourceSummaryLabel = usingManualAge
    ? 'Custom age entry'
    : hasValidOverrideDateOfBirth
      ? `Override DOB ${formatDateDisplay(overrideDateOfBirth)}`
      : hasInvalidOverrideDateOfBirth
        ? 'Invalid override ignored'
        : hasProvidedDateOfBirth
          ? `Provided DOB ${formatDateDisplay(patientProvidedDateOfBirth)}`
          : hasEstimatedDateOfBirth
            ? `Estimated DOB ${formatDateDisplay(patientEstimatedDateOfBirth)}`
            : 'Manual age entry'

  if (!assessmentSpec || !items.length) {
    return (
      <Box p={2}>
        <Typography color="text.secondary">No custom protocol items are available yet for this assessment.</Typography>
      </Box>
    )
  }

  if (submitSuccess) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', alignItems: 'center', p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Assessment submitted
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 440 }}>
          Structured observations are saved. You can review the scored result now or return to the assessment list.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {onViewResult && (
            <Button variant="contained" onClick={() => onViewResult(assessmentId, assessmentSpec?.title, lastRunRef.current)}>
              View result
            </Button>
          )}
          <Button variant="outlined" onClick={handleBackAfterSubmit} startIcon={<ArrowBackIcon />}>
            Back to list
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundImage: (theme) =>
          `linear-gradient(165deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.info.light, 0.1)} 32%, ${alpha(theme.palette.background.default, 0.98)} 100%)`,
      }}
    >
      <Paper
        elevation={0}
        square
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToList}
              sx={{ flexShrink: 0 }}
            >
              Back
            </Button>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
                {runnerTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {runnerSubtitle}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip
              size="small"
              label={`${completedItemCount}/${items.length} captured`}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={
                previewMetrics?.protocolType === 'DST'
                  ? (
                    previewMetrics?.answeredItems > 0 && previewMetrics?.developmentalQuotient != null
                      ? `DQ preview ${previewMetrics.developmentalQuotient}`
                      : 'DQ pending'
                  )
                  : `${previewMetrics?.scorePercentage ?? 0}%`
              }
              color={
                previewMetrics?.protocolType === 'DST'
                  ? (
                    previewMetrics?.answeredItems > 0 && previewMetrics?.developmentalQuotient != null
                      ? 'success'
                      : 'default'
                  )
                  : 'success'
              }
              sx={{ fontWeight: 700 }}
            />
            <TextField
              select
              size="small"
              label="Assist"
              value={assistLanguage || ''}
              onChange={(event) => setAssistLanguage(event.target.value)}
              sx={{ minWidth: 118 }}
            >
              <MenuItem value="">Off</MenuItem>
              {assistLanguageOptions.map((languageCode) => {
                const meta = getAssistLanguageMeta(languageCode)
                return (
                  <MenuItem key={languageCode} value={languageCode}>
                    {`${meta.glyph} ${meta.label}`}
                  </MenuItem>
                )
              })}
            </TextField>
            {latestCompletedRunId && (
              <Button
                size="small"
                variant="text"
                onClick={() => openCompletedResult(latestCompletedRunId)}
                sx={{ minWidth: 0, px: 0.5 }}
              >
                Latest result
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: 'auto', px: 2, pt: 0.9, pb: 2.25 }}>
        {customProtocol?.contentNotice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {customProtocol.contentNotice}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {requiresAgeSetup && !isSetupConfirmed && (
          <Paper
            ref={setupSectionRef}
            elevation={0}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 3,
              border: 1,
              borderColor: alpha('#0f172a', 0.08),
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.96),
              boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Before you begin
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  Review the child&apos;s DOB or enter age manually. Once confirmed, the DST bands will open with this setup fixed at the top.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant={usingManualAge ? 'outlined' : 'contained'}
                  onClick={handleUseDobAge}
                  disabled={!hasUsablePatientDob && !overrideDateOfBirth}
                >
                  Use DOB
                </Button>
                <Button
                  size="small"
                  variant={usingManualAge ? 'contained' : 'outlined'}
                  onClick={handleUseManualAge}
                >
                  Use custom age
                </Button>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
                  gap: 1.25,
                }}
              >
                <TextField
                  size="small"
                  label="DOB override"
                  type="date"
                  value={overrideDateOfBirth}
                  onChange={(event) => {
                    const nextOverrideDateOfBirth = event.target.value
                    setCustomRunData((previous) => ({
                      ...(previous && typeof previous === 'object' ? previous : {}),
                      ageInputMode: nextOverrideDateOfBirth ? 'dob' : (!hasUsablePatientDob ? 'manual' : (previous?.ageInputMode ?? 'dob')),
                      dateOfBirthOverride: nextOverrideDateOfBirth,
                    }))
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                  error={hasInvalidOverrideDateOfBirth}
                  helperText={
                    hasInvalidOverrideDateOfBirth
                      ? 'Invalid or future DOB. It will be ignored.'
                      : !hasUsablePatientDob
                        ? 'No patient DOB is available yet. Enter chronological age below or add a DOB override here.'
                      : hasProvidedDateOfBirth
                        ? `Patient record has provided DOB ${formatDateDisplay(patientProvidedDateOfBirth)}.`
                        : hasEstimatedDateOfBirth
                          ? `Patient record currently has estimated DOB ${formatDateDisplay(patientEstimatedDateOfBirth)}.`
                          : 'Add a DOB override here if you want to use a different DOB for this run.'
                  }
                />
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                    Chronological age
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1,
                    }}
                  >
                    <TextField
                      size="small"
                      label="Years"
                      type="number"
                      value={displayedChronologicalAgeParts.years}
                      onChange={(event) => {
                        const nextYears = event.target.value
                        const totalMonths = buildAgeMonthsFromParts(nextYears, manualAgeParts.months)
                        setCustomRunData((previous) => ({
                          ...previous,
                          ageSource: 'manual-age',
                          chronologicalAgeMonths: totalMonths,
                          chronologicalAgeDays: totalMonths === '' ? '' : totalMonths * 30,
                        }))
                      }}
                      inputProps={{ min: 0 }}
                      InputProps={{ readOnly: !usingManualAge }}
                      disabled={!usingManualAge}
                    />
                    <TextField
                      size="small"
                      label="Months"
                      type="number"
                      value={displayedChronologicalAgeParts.months}
                      onChange={(event) => {
                        const nextMonthsRaw = event.target.value
                        const nextMonths = nextMonthsRaw === '' ? '' : String(Math.min(11, Math.max(0, Number(nextMonthsRaw))))
                        const totalMonths = buildAgeMonthsFromParts(manualAgeParts.years, nextMonths)
                        setCustomRunData((previous) => ({
                          ...previous,
                          ageSource: 'manual-age',
                          chronologicalAgeMonths: totalMonths,
                          chronologicalAgeDays: totalMonths === '' ? '' : totalMonths * 30,
                        }))
                      }}
                      inputProps={{ min: 0, max: 11 }}
                      InputProps={{ readOnly: !usingManualAge }}
                      disabled={!usingManualAge}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {!hasUsablePatientDob && !hasValidOverrideDateOfBirth
                      ? 'No patient DOB is available yet, so enter the chronological age here or add a DOB override above.'
                      : usingManualAge
                      ? 'Enter the practitioner\'s custom age estimate here. Example: 1 year 2 months.'
                      : `Auto-filled from ${ageSourceSummaryLabel.toLowerCase()}. Switch to Use custom age to edit it manually.`}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={ageSourceSummaryLabel} variant="outlined" />
                {actualAgeSectionId && (
                  <Chip size="small" label={`Actual band: ${actualAgeSectionTitle}`} color="info" variant="outlined" />
                )}
                {recommendedSectionId && (
                  <Chip size="small" label={`Start here: ${recommendedSectionTitle}`} color="success" variant="outlined" />
                )}
                {isAgeOutOfRange && ageRangeDisplay && (
                  <Chip size="small" label={`DST range: ${ageRangeDisplay}`} color="warning" />
                )}
              </Box>

              {!canBeginAssessment && (
                <Alert severity="info">
                  Enter a valid DOB or manual age first. The DST bands will appear only after chronological age is available.
                </Alert>
              )}

              {canBeginAssessment && isAgeOutOfRange && ageRangeDisplay && (
                <Alert severity="warning">
                  This age is outside the usual DST range of {ageRangeDisplay}. The runner will open the nearest available band for clinician review.
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="text"
                  onClick={() =>
                    setCustomRunData((previous) => ({
                      ...previous,
                      ageInputMode: 'dob',
                      dateOfBirthOverride: '',
                    }))
                  }
                  disabled={!overrideDateOfBirth && !usingManualAge}
                >
                  Use patient DOB
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmSetup}
                  disabled={!canBeginAssessment}
                >
                  {isAgeOutOfRange ? 'Continue with nearest band' : 'Continue to assessment'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}

        {requiresAgeSetup && canShowAssessmentQuestions && (
          <Paper
            elevation={0}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 4,
              mb: 2,
              px: 1.25,
              py: 0.9,
              borderRadius: 2.5,
              border: 1,
              borderColor: alpha('#0f172a', 0.08),
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
              backdropFilter: 'blur(10px)',
              boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  pb: 0.25,
                  flex: 1,
                  minWidth: 0,
                  '& > *': {
                    flexShrink: 0,
                  },
                }}
              >
                <Chip size="small" label={`Age ${formatAgeDisplayFromMonths(effectiveChronologicalAgeMonths)}`} color="info" />
                <Chip size="small" label={ageSourceSummaryLabel} variant="outlined" />
                {actualAgeSectionId && (
                  <Chip
                    size="small"
                    label={`Band ${actualAgeSectionTitle}`}
                    variant="outlined"
                    onClick={() => scrollToSection(actualAgeSectionId)}
                    clickable
                  />
                )}
                {recommendedSectionId && (
                  <Chip
                    size="small"
                    label={`Start ${recommendedSectionTitle}`}
                    color="success"
                    onClick={() => scrollToSection(recommendedSectionId)}
                    clickable
                  />
                )}
                {isAgeOutOfRange && ageRangeDisplay && (
                  <Chip size="small" label={`Range ${ageRangeDisplay}`} color="warning" />
                )}
              </Box>

              <Stack spacing={0.75} sx={{ ml: 'auto', alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button size="small" variant="outlined" onClick={handleEditSetup}>
                    Edit setup
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        )}

        {canShowAssessmentQuestions && (
        <Stack spacing={2}>
          {domainSections.map((section, sectionIndex) => {
            const domainTitle = resolveDomainTitle(section.domain, primaryLanguage, assistLanguage)
            const isRecommendedSection = recommendedSectionId && recommendedSectionId === section.domain?.domainId
            const isActualAgeSection = actualAgeSectionId && actualAgeSectionId === section.domain?.domainId
            const { startAgeMonths, endAgeMonths } = getSectionAgeRange(section)
            const sectionAgeHint =
              startAgeMonths != null && endAgeMonths != null
                ? `${formatAgeDisplayFromMonths(startAgeMonths)} to ${formatAgeDisplayFromMonths(endAgeMonths)}`
                : null
            const answeredInSection = countAnsweredItems(section.items, structuredResponses)
            const previousSectionIndex = sectionIndex - 1
            const unansweredBeforeThisSection = countUnansweredItemsInSectionRange(
              structuredResponses,
              domainSections,
              previousSectionIndex,
            )
            const answeredBeforeThisSection = countAnsweredItemsInSectionRange(
              structuredResponses,
              domainSections,
              previousSectionIndex,
            )
            return (
              <Paper
                key={section.domain?.domainId ?? `domain-${sectionIndex}`}
                ref={(element) => {
                  if (!section?.domain?.domainId) return
                  if (element) {
                    sectionRefsRef.current[section.domain.domainId] = element
                  } else {
                    delete sectionRefsRef.current[section.domain.domainId]
                  }
                }}
                elevation={0}
                sx={{
                  scrollMarginTop: 16,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: 1,
                  borderColor:
                    isRecommendedSection
                      ? 'success.main'
                      : isActualAgeSection
                        ? 'info.main'
                        : alpha('#0f172a', 0.08),
                  boxShadow: (theme) =>
                    isRecommendedSection
                      ? `0 12px 34px ${alpha(theme.palette.success.main, 0.18)}`
                      : isActualAgeSection
                        ? `0 12px 30px ${alpha(theme.palette.info.main, 0.14)}`
                      : `0 8px 28px ${alpha(theme.palette.common.black, 0.05)}`,
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.4,
                    background: (theme) =>
                      isRecommendedSection
                        ? `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.45)} 0%, ${alpha(theme.palette.info.light, 0.28)} 100%)`
                        : isActualAgeSection
                          ? `linear-gradient(135deg, ${alpha(theme.palette.info.light, 0.5)} 0%, ${alpha(theme.palette.primary.light, 0.28)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.38)} 0%, ${alpha(theme.palette.info.light, 0.26)} 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {domainTitle.primaryTitle}
                      </Typography>
                      {isRecommendedSection && (
                        <Chip size="small" label="Recommended start band" color="success" sx={{ fontWeight: 700 }} />
                      )}
                      {isActualAgeSection && !isRecommendedSection && (
                        <Chip size="small" label="Actual age band" color="info" sx={{ fontWeight: 700 }} />
                      )}
                      <Chip
                        size="small"
                        label={`${answeredInSection}/${section.items.length} answered`}
                        variant="outlined"
                      />
                    </Stack>
                    {protocolType === 'DST' && sectionIndex > 0 ? (
                      <Stack spacing={0.65} sx={{ alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() =>
                            handleEarlierBandsPassClick(
                              previousSectionIndex,
                              domainTitle.primaryTitle,
                              section.domain?.domainId,
                            )
                          }
                          disabled={unansweredBeforeThisSection <= 0 && answeredBeforeThisSection <= 0}
                        >
                          Set earlier bands to PASS
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => handleEarlierBandsReset(previousSectionIndex, section.domain?.domainId)}
                          disabled={answeredBeforeThisSection <= 0}
                        >
                          Clear earlier bands
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} />
                    )}
                  </Box>
                  {domainTitle.assistTitle && domainTitle.assistTitle !== domainTitle.primaryTitle && (
                    <Typography variant="body2" color="text.secondary">
                      {domainTitle.assistTitle}
                    </Typography>
                  )}
                  {sectionAgeHint && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                      DST stage range: {sectionAgeHint}
                    </Typography>
                  )}
                  {isRecommendedSection && (
                    <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                      Start here, then move backward or forward as needed to establish basal and ceiling performance cleanly.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {section.items.map((item) => {
                      const itemCopy = resolveItemCopy(item, primaryLanguage, assistLanguage)
                      const response = structuredResponses?.[item?.itemCode] ?? {}
                      const resolvedHints = Array.isArray(customProtocol?.resolvedHintsByItemCode?.[item?.itemCode])
                        ? customProtocol.resolvedHintsByItemCode[item.itemCode]
                        : []
                      const statusOptions = Array.isArray(customProtocol?.responseModes?.statuses)
                        ? customProtocol.responseModes.statuses
                        : ['pass', 'fail', 'not_testable']
                      const evidenceModes = Array.isArray(customProtocol?.responseModes?.evidenceModes)
                        ? customProtocol.responseModes.evidenceModes
                        : ['observation', 'caregiver_report', 'mixed']
                      const activeStatus = response?.status ?? ''
                      const itemAgeDisplay =
                        item?.config?.ageEquivalentDisplay ??
                        item?.ageBand ??
                        item?.config?.ageLabel ??
                        null
                      const primarySubTextTrimmed = typeof itemCopy.primarySubText === 'string' ? itemCopy.primarySubText.trim() : ''
                      const assistSubTextTrimmed = typeof itemCopy.assistSubText === 'string' ? itemCopy.assistSubText.trim() : ''
                      const isAgeEquivalentOnlySubText =
                        Boolean(itemAgeDisplay) &&
                        /^age equivalent\s*:/i.test(primarySubTextTrimmed)
                      const showPrimarySubText = Boolean(itemCopy.primarySubText) && !isAgeEquivalentOnlySubText
                      const showAssistSubText =
                        Boolean(itemCopy.assistSubText) &&
                        !isAgeEquivalentOnlySubText &&
                        assistSubTextTrimmed !== primarySubTextTrimmed

                      return (
                        <Paper
                          key={item?.itemCode}
                          elevation={0}
                          sx={{
                            p: 1.75,
                            borderRadius: 2.5,
                            border: 1,
                            borderColor:
                              activeStatus && STATUS_COLORS[activeStatus]
                                ? `${STATUS_COLORS[activeStatus]}.light`
                                : 'divider',
                            bgcolor: (theme) => alpha(theme.palette.background.default, 0.72),
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {item?.superText && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textTransform: 'uppercase',
                                    letterSpacing: 1.1,
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    mb: 0.75,
                                  }}
                                >
                                  {item.superText}
                                </Typography>
                              )}
                              <Typography variant="body1" fontWeight={700} color="text.primary">
                                {itemCopy.primaryLabel}
                              </Typography>
                              {itemCopy.assistLabel && itemCopy.assistLabel !== itemCopy.primaryLabel && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {itemCopy.assistLabel}
                                </Typography>
                              )}
                              {itemAgeDisplay && (
                                <Chip
                                  size="small"
                                  label={`Age ${itemAgeDisplay}`}
                                  color="info"
                                  variant="outlined"
                                  sx={{ mt: 1, mr: 1, fontWeight: 700 }}
                                />
                              )}
                              {item?.itemCode && (
                                <Chip
                                  size="small"
                                  label={item.itemCode}
                                  variant="outlined"
                                  sx={{ mt: 1, fontWeight: 700, bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85) }}
                                />
                              )}
                            </Box>
                            {itemCopy.primaryHelpText && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2">{itemCopy.primaryHelpText}</Typography>
                                    {itemCopy.assistHelpText && itemCopy.assistHelpText !== itemCopy.primaryHelpText && (
                                      <Typography variant="body2" sx={{ mt: 1, opacity: 0.86 }}>
                                        {itemCopy.assistHelpText}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <IconButton size="small" aria-label="Item help">
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {showPrimarySubText && (
                            <Paper
                              elevation={0}
                              sx={{
                                mt: 1.25,
                                p: 1.1,
                                borderRadius: 1.5,
                                bgcolor: (theme) => alpha(theme.palette.info.light, 0.14),
                                borderLeft: '3px solid',
                                borderColor: 'info.main',
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {itemCopy.primarySubText}
                              </Typography>
                              {showAssistSubText && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                  {itemCopy.assistSubText}
                                </Typography>
                              )}
                            </Paper>
                          )}

                          {resolvedHints.length > 0 && (
                            <Stack spacing={1} sx={{ mt: 1.25 }}>
                              {resolvedHints.map((hint) => {
                                const assistHintText =
                                  assistLanguage && assistLanguage !== primaryLanguage
                                    ? hint?.translations?.[assistLanguage]
                                    : null
                                return (
                                  <Paper
                                    key={hint?.id ?? hint?.title}
                                    elevation={0}
                                    sx={{
                                      p: 1.1,
                                      borderRadius: 1.5,
                                      border: 1,
                                      borderColor: alpha('#0369a1', 0.18),
                                      bgcolor: alpha('#e0f2fe', 0.65),
                                    }}
                                  >
                                    {hint?.title && (
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#075985', mb: 0.5 }}>
                                        {hint.title}
                                      </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: '#0f172a' }}>
                                      {hint?.text}
                                    </Typography>
                                    {assistHintText && assistHintText !== hint?.text && (
                                      <Typography variant="body2" sx={{ mt: 0.75, color: '#334155' }}>
                                        {assistHintText}
                                      </Typography>
                                    )}
                                  </Paper>
                                )
                              })}
                            </Stack>
                          )}

                          <Divider sx={{ my: 1.5 }} />

                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1.5 }}>
                            {statusOptions.map((status) => {
                              const selected = activeStatus === status
                              return (
                                <Button
                                  key={status}
                                  size="small"
                                  variant={selected ? 'contained' : 'outlined'}
                                  color={STATUS_COLORS[status] ?? 'primary'}
                                  onClick={() =>
                                    updateStructuredResponse(item.itemCode, {
                                      status: selected ? '' : status,
                                    })
                                  }
                                  sx={{
                                    borderRadius: 999,
                                    px: 1.4,
                                    fontWeight: 700,
                                    boxShadow: selected ? 2 : 'none',
                                  }}
                                >
                                  {formatStatusLabel(status)}
                                </Button>
                              )
                            })}
                          </Stack>

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
                              gap: 1.25,
                            }}
                          >
                            <TextField
                              select
                              size="small"
                              label="Evidence source"
                              value={response?.evidence ?? ''}
                              onChange={(event) =>
                                updateStructuredResponse(item.itemCode, {
                                  evidence: event.target.value,
                                })
                              }
                            >
                              <MenuItem value="">Not set</MenuItem>
                              {evidenceModes.map((mode) => (
                                <MenuItem key={mode} value={mode}>
                                  {formatStatusLabel(mode)}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              size="small"
                              label="Examiner notes"
                              value={response?.notes ?? ''}
                              onChange={(event) =>
                                updateStructuredResponse(item.itemCode, {
                                  notes: event.target.value,
                                })
                              }
                              multiline
                              minRows={2}
                            />
                          </Box>
                        </Paper>
                      )
                    })}
                  </Stack>
                </Box>
              </Paper>
            )
          })}
        </Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(earlierBandsDialog)}
        onClose={() => setEarlierBandsDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update earlier bands?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Some milestones before <strong>{earlierBandsDialog?.currentSectionTitle ?? 'this band'}</strong> are already marked as
            {' '}Fail or Not testable.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            If you now want to treat all earlier developmental levels as already achieved, convert every earlier milestone to PASS.
            Otherwise, only fill the unanswered earlier milestones and keep the existing results as they are.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setEarlierBandsDialog(null)}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => confirmEarlierBandsPass(false)}
          >
            Only fill unanswered earlier milestones
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => confirmEarlierBandsPass(true)}
          >
            Convert all earlier milestones to PASS
          </Button>
        </DialogActions>
      </Dialog>

      {canShowAssessmentQuestions && (
        <Paper
          elevation={0}
          square
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
          }}
        >
          <Stack direction="row" spacing={1.25} justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              startIcon={<ReplayIcon />}
              onClick={clearAllResponses}
              disabled={submitting}
              sx={{ flexShrink: 0 }}
            >
              Reset
            </Button>
            {protocolType === 'DST' && (
              <Box
                sx={{
                  ml: 'auto',
                  mr: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  border: 1,
                  borderColor:
                    previewMetrics?.maxConsecutiveFailCount > 5
                      ? alpha('#dc2626', 0.34)
                      : alpha('#0f172a', 0.1),
                  bgcolor:
                    previewMetrics?.maxConsecutiveFailCount > 5
                      ? alpha('#fee2e2', 0.88)
                      : alpha('#f8fafc', 0.92),
                  boxShadow:
                    previewMetrics?.maxConsecutiveFailCount > 5
                      ? `0 0 0 4px ${alpha('#fecaca', 0.34)}`
                      : 'none',
                  animation:
                    previewMetrics?.maxConsecutiveFailCount > 5
                      ? 'dstFailStreakPulse 1s ease-in-out infinite'
                      : 'none',
                  '@keyframes dstFailStreakPulse': {
                    '0%': {
                      backgroundColor: alpha('#fee2e2', 0.78),
                      boxShadow: `0 0 0 0 ${alpha('#f87171', 0.16)}`,
                    },
                    '50%': {
                      backgroundColor: alpha('#fecaca', 0.98),
                      boxShadow: `0 0 0 6px ${alpha('#f87171', 0.28)}`,
                    },
                    '100%': {
                      backgroundColor: alpha('#fee2e2', 0.78),
                      boxShadow: `0 0 0 0 ${alpha('#f87171', 0.16)}`,
                    },
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 800,
                    color:
                      previewMetrics?.maxConsecutiveFailCount > 5
                        ? 'error.dark'
                        : 'text.primary',
                  }}
                >
                  Max consecutive FAIL: {previewMetrics?.maxConsecutiveFailCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                  {previewMetrics?.maxConsecutiveFailEndAgeDisplay
                    ? `Ceiling reached at ${previewMetrics.maxConsecutiveFailEndAgeDisplay}`
                    : 'Ceiling age will appear once FAIL milestones are recorded'}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minWidth: 210, ml: 'auto' }}
            >
              {submitting ? 'Submitting...' : 'Submit assessment'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  )
}
